import { ITask } from "@/interfaces/dashboard.interface";
import AvatarGroup from "./avatar-group";
import { Pencil } from "lucide-react";
import { cn } from "@/lib/class-control";

interface TaskCardProps {
  task: ITask;
  onEdit: (task: ITask) => void;
  onView?: (task: ITask) => void;
}

export default function TaskCard({ task, onEdit, onView }: TaskCardProps) {
  // Calculate days ago from createdAt
  const getDaysAgo = (createdAt?: Date): number => {
    if (!createdAt) return 0;
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - createdAt.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysAgo = getDaysAgo(task.createdAt);

  // Determine time badge color based on days
  const getTimeBadgeColor = (days: number): string => {
    if (days <= 3) return "#13D480"; // Green
    if (days <= 7) return "#F2C71E"; // Yellow
    return "#DC4426"; // Red
  };

  const timeBadgeColor = getTimeBadgeColor(daysAgo);

  return (
    <div
      onClick={() => onView?.(task)}
      className="bg-[#15203C] w-[359px] h-[207px] rounded-[20px] px-[30px] py-[20px] flex flex-col gap-[14px] cursor-pointer hover:bg-[#1a2847] transition-colors"
    >
      {/* Tags and Title */}
      <div className="flex flex-col gap-[10px] w-full">
        {/* Tag Bars */}
        <div className="flex gap-[10px]">
          {task.tags?.slice(0, 2).map((tag, index) => (
            <div
              key={index}
              className="h-[9px] w-[67px] rounded-[40px]"
              style={{ backgroundColor: tag.color }}
            />
          ))}
        </div>

        {/* Title Row with Edit Icon */}
        <div className="flex gap-[10px] items-center justify-between w-full">
          <p className="flex-1 font-['Plus_Jakarta_Sans'] font-bold text-[20px] text-white leading-normal">
            {task.taskName}
          </p>
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering card click
              onEdit(task);
            }}
            className="w-[24px] h-[24px] flex-shrink-0 text-white hover:text-gray-300 transition-colors"
            title="Editar tarefa"
          >
            <Pencil className="w-full h-full" />
          </button>
        </div>
      </div>

      {/* Description */}
      <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] text-[#D1D1D1] h-[61px] leading-normal overflow-hidden">
        {task.description || ""}
      </p>

      {/* Bottom Row: Avatars and Time Badge */}
      <div className="flex gap-[157px] items-start justify-between">
        {/* Avatars */}
        {task.assignees && task.assignees.length > 0 && (
          <AvatarGroup assignees={task.assignees} maxAvatars={4} />
        )}

        {/* Time Badge */}
        <div
          className={cn(
            "bg-[#15203C] h-[23px] rounded-[20px] px-[10px] flex items-center justify-center",
            "font-['Plus_Jakarta_Sans'] font-normal text-[12px] leading-normal whitespace-nowrap"
          )}
          style={{ color: timeBadgeColor }}
        >
          {daysAgo === 0 ? "Hoje" : daysAgo === 1 ? "1 dia atrás" : `${daysAgo} dias atrás`}
        </div>
      </div>
    </div>
  );
}
